import { Token } from "@mui/icons-material";
import { applySnapshot, flow, types } from "mobx-state-tree";
import { ErrorModel } from "../../components/common/error";
import { MessageModel } from "../../components/common/message";
import i18n from "../../i18n";
import { IInput } from "../../utils/common-interface";
import orgAPI from "../org/OrgService";
import authAPI from "./AuthService";
import { IUserModel, UserModel } from "./UserModel";

export const ForgetPasswordModel = types
  .model("ForgetPasswordModel", {
    userName: types.optional(types.string, ""),
    email: types.optional(types.string, ""),
    pin1: types.optional(types.string, ""),
    pin2: types.optional(types.string, ""),
    pin3: types.optional(types.string, ""),
    pin4: types.optional(types.string, ""),
    pin5: types.optional(types.string, ""),
    pin6: types.optional(types.string, ""),
    password: types.optional(types.string, ""),
    confirmPassword: types.optional(types.string, ""),
    error: types.optional(ErrorModel, {}),
    loading: types.optional(types.boolean, false),
    alert: types.optional(MessageModel, {})
  })
  .views((self: any) => ({
    get pinCode() {
      return (
        self.pin1 + self.pin2 + self.pin3 + self.pin4 + self.pin5 + self.pin6
      );
    }
  }))
  .actions((self: any) => ({
    sendPinCode: flow(function*() {
      try {
        const result = yield authAPI.sendPinCode(self.userName);
        self.error.tigger = false;
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        //
      }
    }),
    checkPinIsValid: flow(function*() {
      try {
        const result = yield authAPI.checkPinIsValid({
          userName: self.userName,
          pin: self.pinCode
        });
        self.error.tigger = false;
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        //
      }
    }),
    changePassword: flow(function*(firstTimeAccess: boolean) {
      try {
        const result = yield authAPI.changePassword({
          userName: self.userName,
          pin: self.pinCode,
          newPassword: self.password
        });

        self.error.tigger = false;
        self.alert.tigger = true;
        self.alert.title = i18n.t("authModel.finishingSubmit");
        self.alert.message = firstTimeAccess
          ? i18n.t("authModel.firstTimeRegisteredSuccess")
          : i18n.t("authModel.forgetPasswordSuccess");
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        //
      }
    }),
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
    resetAll: () => {
      applySnapshot(self, {});
    }
  }));
export type IForgetPasswordModel = typeof ForgetPasswordModel.Type;

export const AuthModel = types
  .model("AuthModel", {
    userName: types.optional(types.string, ""),
    password: types.optional(types.string, ""),
    token: types.optional(types.string, ""),
    id: types.optional(types.string, ""),
    permissions: types.optional(types.array(types.string), []),
    userProfile: types.optional(UserModel, {}),
    error: types.optional(ErrorModel, {}),
    loading: types.optional(types.boolean, false)
  })
  .views((self: any) => ({
    get fullname() {
      const space =
        self.userProfile.tFirstName && self.userProfile.tLastName ? " " : "";
      return `${self.userProfile.tFirstName || ""}${space}${self.userProfile
        .tLastName || ""}`;
    }
  }))
  .actions((self: any) => ({
    sign_in: flow(function*() {
      try {
        const result = yield authAPI.sign_in({
          userName: self.userName,
          password: self.password
        });
        window.localStorage.setItem(
          "access_token",
          result.data.jwtAccessToken || ""
        );
        window.localStorage.setItem(
          "userProfile",
          JSON.stringify(result.data.userProfile)
        );
        window.localStorage.setItem(
          "permissions",
          JSON.stringify(result.data.permissions)
        );
        self.setAllField(result.data);
        self.error.tigger = false;
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        //
      }
    }),
    token_sign_in: flow(function*() {
      try {
        const result = yield authAPI.token_sign_in({
          userName: self.userName,
          password: self.password,
        });
        window.localStorage.setItem(
          "access_token",
          result.data.jwtAccessToken || ""
        );
        window.localStorage.setItem(
          "userProfile",
          JSON.stringify(result.data.userProfile)
        );
        window.localStorage.setItem(
          "permissions",
          JSON.stringify(result.data.permissions)
        );
        self.setAllField(result.data);
        self.error.tigger = false;
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        //
      }
    }),    
    sign_out: flow(function*() {
      try {
        self.loading = true;
        yield new Promise((resolve) => setTimeout(resolve, 1000));
        self.resetAll();
        window.localStorage.removeItem("access_token");
        window.localStorage.removeItem("uid");
      } catch (e) {
        throw e;
      } finally {
        self.loading = false;
      }
    }),
    getOrganizeOfAuth: flow(function*() {
      try {
        const result = yield orgAPI.getById(self.userProfile.organizationID);
        self.userProfile.organizationTHName = result.data.organizationTHName;
        self.error.tigger = false;
      } catch (e) {
        self.error.tigger = true;
        self.error.code = e.code;
        self.error.title = e.name;
        self.error.message = e.message;
        self.error.technicalStack = e.technicalStack;
        throw e;
      } finally {
        //
      }
    }),
    setAuthDataFromLocalStorage: () => {
      const userProfileLocal = window.localStorage.getItem("userProfile");
      if (userProfileLocal) {
        const userProfile: IUserModel = JSON.parse(userProfileLocal);
        self.id = userProfile.id;
        self.userProfile = userProfile;
        const permissions = window.localStorage.getItem("permissions");
        self.permissions = permissions ? JSON.parse(permissions) : [];
        // console.log("setAuthData", self.userProfile);
      } else {
        console.log("userProfile not in localStorageYet");
      }
    },
    setAllField: (data: any) => {
      Object.keys(data).forEach((key) => {
        try {
          self[key] = data[key];
        } catch (e) {
          console.log("exception on mapping key:", key);
        }
      });
    },
    setField: ({ fieldname, value }: IInput) => {
      self[fieldname] = value;
    },
    resetAll: () => {
      applySnapshot(self, {});
    }
  }));
export type IAuthModel = typeof AuthModel.Type;
export default AuthModel.create();